import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  ShieldX,
  Globe,
  CreditCard,
  Newspaper,
  FileSearch,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const verificationTypes = [
  { id: "website", label: "Website", icon: Globe, placeholder: "Paste a URL to verify (e.g., https://example.com)" },
  { id: "transaction", label: "Transaction", icon: CreditCard, placeholder: "Paste transaction details, offer, or payment request..." },
  { id: "news", label: "News", icon: Newspaper, placeholder: "Paste news article text or URL to verify..." },
  { id: "source", label: "Source", icon: FileSearch, placeholder: "Paste content to check its source and authenticity..." }
];

const trustLevelConfig = {
  trusted: { 
    color: "bg-green-100 text-green-800 border-green-200", 
    icon: ShieldCheck, 
    label: "Trusted",
    bgColor: "bg-green-50",
    borderColor: "border-green-200"
  },
  likely_safe: { 
    color: "bg-blue-100 text-blue-800 border-blue-200", 
    icon: Shield, 
    label: "Likely Safe",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200"
  },
  caution: { 
    color: "bg-yellow-100 text-yellow-800 border-yellow-200", 
    icon: ShieldAlert, 
    label: "Use Caution",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200"
  },
  suspicious: { 
    color: "bg-orange-100 text-orange-800 border-orange-200", 
    icon: ShieldAlert, 
    label: "Suspicious",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200"
  },
  dangerous: { 
    color: "bg-red-100 text-red-800 border-red-200", 
    icon: ShieldX, 
    label: "Dangerous",
    bgColor: "bg-red-50",
    borderColor: "border-red-200"
  }
};

const flagIcons = {
  success: CheckCircle,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info
};

const flagColors = {
  success: "text-green-600 bg-green-50",
  warning: "text-yellow-600 bg-yellow-50",
  danger: "text-red-600 bg-red-50",
  info: "text-blue-600 bg-blue-50"
};

export const TrustVerifier = () => {
  const [activeType, setActiveType] = useState("website");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleVerify = async () => {
    if (!content.trim()) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: content.trim(),
          verification_type: activeType
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        setResult({ error: "Verification failed. Please try again." });
      }
    } catch (error) {
      console.error("Verification error:", error);
      setResult({ error: "Unable to connect to verification service." });
    }
    
    setLoading(false);
  };

  const activeTypeConfig = verificationTypes.find(t => t.id === activeType);
  const trustConfig = result?.trust_level ? trustLevelConfig[result.trust_level] : null;
  const TrustIcon = trustConfig?.icon || Shield;

  return (
    <div className="w-full" data-testid="trust-verifier">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-medium mb-3">
          <Shield className="w-4 h-4" />
          FREE TOOL
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Trust Verifier</h2>
        <p className="text-slate-600 max-w-xl mx-auto">
          Check if websites, transactions, news, or information sources are legitimate and trustworthy
        </p>
      </div>

      {/* Verification Type Tabs */}
      <div className="flex justify-center gap-2 mb-6 flex-wrap">
        {verificationTypes.map((type) => {
          const Icon = type.icon;
          return (
            <button
              key={type.id}
              onClick={() => {
                setActiveType(type.id);
                setResult(null);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                activeType === type.id
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
              }`}
              data-testid={`verify-type-${type.id}`}
            >
              <Icon className="w-4 h-4" />
              {type.label}
            </button>
          );
        })}
      </div>

      {/* Input Area */}
      <Card className="border-slate-200 shadow-lg max-w-3xl mx-auto">
        <CardContent className="p-6">
          <Textarea
            placeholder={activeTypeConfig?.placeholder}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-[120px] text-base mb-4 resize-none"
            data-testid="verify-input"
          />
          
          <Button
            onClick={handleVerify}
            disabled={loading || !content.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base"
            data-testid="verify-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 mr-2" />
                Verify {activeTypeConfig?.label}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && !result.error && (
        <Card className={`mt-6 max-w-3xl mx-auto border-2 ${trustConfig?.borderColor}`} data-testid="verify-result">
          <CardContent className="p-6">
            {/* Trust Score Header */}
            <div className={`flex items-center justify-between p-4 rounded-xl ${trustConfig?.bgColor} mb-6`}>
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${trustConfig?.color}`}>
                  <TrustIcon className="w-8 h-8" />
                </div>
                <div>
                  <Badge className={trustConfig?.color}>{trustConfig?.label}</Badge>
                  <p className="text-2xl font-bold mt-1">{result.trust_score}% Trust Score</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">Verification Type</div>
                <div className="font-medium capitalize">{result.verification_type}</div>
              </div>
            </div>

            {/* Summary */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-900 mb-2">Summary</h3>
              <p className="text-slate-700">{result.summary}</p>
            </div>

            {/* Flags */}
            {result.flags && result.flags.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3">Findings</h3>
                <div className="space-y-2">
                  {result.flags.map((flag, index) => {
                    const FlagIcon = flagIcons[flag.flag_type] || Info;
                    return (
                      <div 
                        key={index} 
                        className={`flex items-center gap-3 p-3 rounded-lg ${flagColors[flag.flag_type]}`}
                      >
                        <FlagIcon className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{flag.message}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations && result.recommendations.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3">Recommendations</h3>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start gap-2 text-slate-700">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Details Toggle */}
            {result.details && Object.keys(result.details).length > 0 && (
              <div>
                <button 
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm"
                >
                  {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showDetails ? "Hide" : "Show"} Technical Details
                </button>
                
                {showDetails && (
                  <div className="mt-3 p-4 bg-slate-50 rounded-lg">
                    <pre className="text-xs text-slate-600 overflow-x-auto">
                      {JSON.stringify(result.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {result?.error && (
        <Card className="mt-6 max-w-3xl mx-auto border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-700">{result.error}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TrustVerifier;
